import { NgFor, NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AlertFacade } from '@curbnturf/alert';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import { IListing } from '@curbnturf/entities';
import { BlackoutSelectorComponent } from '@curbnturf/form-helpers';
import { listingComplete } from '@curbnturf/helpers';
import { ListingFacade } from '@curbnturf/listing';
import { IonicModule } from '@ionic/angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { Info } from 'luxon';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'curbnturf-blackout',
  templateUrl: './blackout.component.html',
  styleUrls: ['./blackout.component.scss'],
  standalone: true,
  imports: [
    BlackoutSelectorComponent,
    FormsModule,
    IonicModule,
    MiscSvgComponent,
    NgIf,
    NgSelectModule,
    NgFor,
    ReactiveFormsModule,
  ],
})
export class BlackoutComponent implements OnDestroy, OnInit {
  @Input() listing: IListing;

  form: FormGroup<{
    blackoutMonths: FormArray<FormControl<number | null>>;
    blackoutDays: FormArray<FormControl<string | null>>;
    blackoutDayOfWeek: FormArray<FormControl<string | null>>;
  }>;

  listingComplete = listingComplete;

  blackoutDaysRecount = 0;
  availableMonths: { label: string; unit: number }[] = [];
  availableDaysOfWeek: string[] = [];
  blackoutOpen = false;

  get blackoutDays() {
    if (this.form) {
      return this.form.controls.blackoutDays;
    }

    return;
  }

  get blackoutMonths() {
    if (this.form) {
      return this.form.controls.blackoutMonths;
    }

    return;
  }

  get blackoutDayOfWeek() {
    if (this.form) {
      return this.form.controls.blackoutDayOfWeek;
    }

    return;
  }

  readonly months = Info.months('long').map((month, index) => {
    return { label: month, unit: index + 1 };
  });

  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private listingFacade: ListingFacade,
    private alertFacade: AlertFacade,
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      blackoutMonths: this.fb.array<FormControl<number | null>>([]),
      blackoutDays: this.fb.array<FormControl<string | null>>([]),
      blackoutDayOfWeek: this.fb.array<FormControl<string | null>>([]),
    });

    this.form.controls.blackoutDays.clear();
    this.listing.blackoutDays?.forEach((day) => {
      this.form.controls.blackoutDays.push(this.fb.control<string | null>(day));
    });

    this.form.controls.blackoutMonths.clear();
    this.listing.blackoutMonths?.forEach((month) => {
      this.form.controls.blackoutMonths.push(this.fb.control<number | null>(month));
    });

    this.form.controls.blackoutDayOfWeek.clear();
    this.listing.blackoutDayOfWeek?.forEach((day) => {
      this.form.controls.blackoutDayOfWeek.push(this.fb.control<string | null>(day));
    });

    this.availableMonths = this.availableBlackoutMonths();

    this.subscriptions.push(
      this.form.valueChanges.pipe(debounceTime(1000)).subscribe(() => {
        this.updateBlackoutTimes();
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  updateBlackoutTimes() {
    this.form.markAsTouched();
    this.form.markAsDirty();

    if (this.form.valid && this.listing.id) {
      // Update the user record with the following data
      this.listingFacade.updateBlackoutTimes(this.listing.id, {
        blackoutMonths: (this.blackoutMonths?.value.filter((el) => el) as number[]) ?? [],
        blackoutDays: (this.blackoutDays?.value.filter((el) => el) as string[]) ?? [],
        blackoutDayOfWeek: (this.blackoutDayOfWeek?.value.filter((el) => el) as string[]) ?? [],
      });
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  addBlackoutMonth() {
    if (this.blackoutMonths) {
      this.blackoutMonths.push(this.fb.control<number | null>(null));
      this.availableMonths = this.availableBlackoutMonths();
    }
  }

  addBlackoutDay() {
    if (this.blackoutDays) {
      this.blackoutDays.push(this.fb.control<string | null>(''));
    }
  }

  addBlackoutDayOfWeek() {
    if (this.blackoutDayOfWeek) {
      this.blackoutDayOfWeek.push(this.fb.control<string | null>(null));
      this.availableDaysOfWeek = this.availableBlackoutDaysOfWeek();
    }
  }

  blackoutMonthRemove(index: number) {
    if (this.blackoutMonths) {
      this.blackoutMonths.removeAt(index);
      this.blackoutMonths.markAsDirty();
      this.availableMonths = this.availableBlackoutMonths();
    }
  }

  blackoutDayOfWeekRemove(index: number) {
    if (this.blackoutDayOfWeek) {
      this.blackoutDayOfWeek.removeAt(index);
      this.blackoutDayOfWeek.markAsDirty();
      this.availableDaysOfWeek = this.availableBlackoutDaysOfWeek();
    }
  }

  blackoutTrackBy(): (index: number, blackout: string) => number {
    // need access to this.blackoutDaysRecount
    return (index: number): number => index + this.blackoutDaysRecount;
  }

  blackoutUpdated(blackout: string, index: number) {
    if (this.blackoutDays) {
      this.blackoutDays.controls[index].setValue(blackout);
      this.blackoutDays.markAsDirty();
    }
  }

  blackoutRemove(index: number) {
    if (this.blackoutDays) {
      // to trigger blackout ng-for to re-render
      this.blackoutDaysRecount += this.blackoutDays.controls.length;

      this.blackoutDays.removeAt(index);
      this.blackoutDays.markAsDirty();
    }
  }

  private availableBlackoutMonths(): { label: string; unit: number }[] {
    const selectedMonths =
      this.blackoutMonths?.controls.map((month) => {
        return month.value ?? 1 - 1;
      }) ?? [];
    return Info.months('long')
      .filter((el, index) => !selectedMonths.includes(index + 1))
      .map((month) => {
        const completeMonths = Info.months('long');
        return { label: month, unit: completeMonths.indexOf(month) + 1 };
      });
  }

  private availableBlackoutDaysOfWeek(): string[] {
    const selectedDays =
      this.blackoutDayOfWeek?.controls
        .filter((el) => el.value !== null)
        .map((day) => {
          return day.value;
        }) ?? [];
    return Info.weekdays().filter((el) => !selectedDays.includes(el));
  }
}
